import Header from 'components/Headers/Header';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { EMRS, NOTE_TYPES } from '../constants/EmrNoteTypeMapping';
import { getFirstEmrNoteType } from '../components/Misc/utils';

const SECTION_TITLES = [
  'Browse by EMRs',
  'Browse by Note Type',
  'Top 15 Most Used Notes',
  'Browse by Discipline',
];

// Displays a grid of cards based on the selected category (EMRs, Note Types, or Most Used)

const TemplateLibraryBrowseByCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiClient } = useContext(GlobalContext);

  const queryParams = new URLSearchParams(location.search);
  const currentTab = parseInt(queryParams.get('index'));
  const currentEmr = queryParams.get('emr');
  const currentNote = queryParams.get('note');
  const currentProfession = queryParams.get('profession');
  const seeAll = queryParams.get('see-all');

  const [mostUsed, setMostUsed] = useState([]);
  const [emrs, setEmrs] = useState([]);
  const [notes, setNotes] = useState(NOTE_TYPES);
  const [isLoading, setIsLoading] = useState(true);

  // Save current path for navigation
  useEffect(() => {
    sessionStorage.setItem(
      'Library-Browse-Path',
      location.pathname + location.search,
    );
  }, [location]);

  // Fetch data when tab changes
  useEffect(() => {
    fetchCardData();
  }, [currentTab]);

  /**
   * Fetches and processes data based on the current view
   * Handles both "See All" and filtered views
   */
  const fetchCardData = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.searchConfigsLibrary(
        seeAll ? undefined : currentEmr,
        seeAll ? undefined : currentNote,
        seeAll ? undefined : currentProfession,
      );

      if (seeAll) {
        await processSeeAllData(data);
      } else {
        await processFilteredData(data);
      }
    } catch (error) {
      console.error('Error fetching template data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const processSeeAllData = async (data) => {
    // Process most used templates
    if (data?.configs?.top_configs?.length) {
      const emrLogos = data.configs.top_configs.reduce((acc, note) => {
        if (note.emr) {
          acc[note.emr] = EMRS.find((e) => e.name === note.emr)?.image || '';
        }
        return acc;
      }, {});

      const formattedMostUsed = data.configs.top_configs.map((note) => ({
        emr: note.emr,
        name: getFirstEmrNoteType(note, note?.name || 'Template'),
        image: emrLogos[note.emr],
        libraryConfigId: note.config_library_id,
      }));
      setMostUsed(formattedMostUsed);
    }

    if (data?.emrs?.length > 0) {
      const processedEmrs = data.emrs.sort().map((emr) => ({
        name: emr,
        image: EMRS.find((e) => e.name === emr)?.image || '',
      }));
      setEmrs(processedEmrs);
    }
  };

  const processFilteredData = async (data) => {
    if (currentTab === 0 && data?.configs?.[currentNote]) {
      const emrNames = [
        ...new Set(data.configs[currentNote].map((config) => config.emr)),
      ];
      const processedEmrs = emrNames.map((emr) => ({
        name: emr,
        image: EMRS.find((e) => e.name === emr)?.image || '',
      }));
      setEmrs(processedEmrs);
    } else if (currentTab === 1 && data?.configs?.[currentEmr]) {
      const noteTypes = [
        ...new Set(
          data.configs[currentEmr]
            .map((config) => getFirstEmrNoteType(config, config?.name || ''))
            .filter(Boolean),
        ),
      ];
      const formattedNotes = noteTypes.map((note) => ({
        name: note,
        image: NOTE_TYPES.find((n) => n.name === note)?.image || '',
      }));
      setNotes(formattedNotes);
    } else if (currentTab === 3 && data?.configs?.[currentProfession]) {
      // Handle profession filtering - data comes in data.configs[currentProfession] format
      const professionConfigs = data.configs[currentProfession];
      if (professionConfigs && professionConfigs.length > 0) {
        // Get unique EMRs for this profession
        const emrNames = [
          ...new Set(professionConfigs.map((config) => config.emr)),
        ];
        const processedEmrs = emrNames.map((emr) => ({
          name: emr,
          image: EMRS.find((e) => e.name === emr)?.image || '',
        }));
        setEmrs(processedEmrs);
      }
    }
  };

  const handleCardClick = (item, tabIndex) => {
    if (currentTab === 3) {
      // Handle profession-based navigation - EMR card clicked in profession view
      navigate(
        `/User/template/library/list?profession=${currentProfession}&emr=${item.name}`,
      );
    } else if (tabIndex === 0) {
      navigate(
        seeAll
          ? `/User/template/library/browse?emr=${item.name}&index=${1}`
          : `/User/template/library/list?note=${currentNote}&emr=${item.name}`,
      );
    } else if (tabIndex === 1) {
      navigate(
        seeAll
          ? `/User/template/library/browse?note=${item.name}&index=${0}`
          : `/User/template/library/list?emr=${currentEmr}&note=${item.name}`,
      );
    } else if (tabIndex === 2) {
      navigate(
        `/User/template/library/details?configId=${item.libraryConfigId}&emr=${item.emr}`,
      );
      sessionStorage.setItem('Library-List-Path', '');
    }
  };

  const renderCard = (item, tabIndex) => {
    const cardContent = {
      0: (
        <div
          className="emr-content"
          onClick={() => handleCardClick(item, tabIndex)}
        >
          <img src={item.image} alt={item.name} className="emr-logo" />
          <h6 className="note-name">{item.name}</h6>
        </div>
      ),
      1: (
        <div
          className="note-content"
          onClick={() => handleCardClick(item, tabIndex)}
        >
          <img src={item.image} alt={item.name} className="note-image" />
          <h6 className="note-name">{item.name}</h6>
        </div>
      ),
      2: (
        <div
          className="most-used-content"
          onClick={() => handleCardClick(item, tabIndex)}
        >
          <img
            src={item.image}
            alt={item.name}
            className="most-used-emr-logo"
          />
          <h6 className="most-used-text">{item.name}</h6>
        </div>
      ),
    };

    return (
      <Card
        className={`custom-card text-center p-2 ${tabIndex === 1 ? 'note-card' : tabIndex === 2 ? 'most-used-card' : 'emr-card'}`}
        style={{ cursor: 'pointer' }}
      >
        {cardContent[tabIndex]}
      </Card>
    );
  };

  // Determine which data to display based on current tab
  const sectionData =
    currentTab === 0
      ? emrs
      : currentTab === 1
        ? notes
        : currentTab === 3
          ? emrs
          : mostUsed;

  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow border-0">
              <CardHeader className="border border-2 border-grey bg-white text-left d-flex align-items-center">
                <Button
                  className="fa fa-arrow-left"
                  color="primary"
                  size="md"
                  title="All Templates"
                  onClick={() => navigate('/User/template/library')}
                />
                <h2 className="mb-0 font-weight-bold ml-3">
                  {currentTab === 3
                    ? currentProfession
                      ? 'Browse by EMR'
                      : SECTION_TITLES[currentTab]
                    : SECTION_TITLES[currentTab]}
                </h2>
              </CardHeader>
              <Container className="mt-4" fluid>
                {isLoading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                    <p>Loading...</p>
                  </div>
                ) : (
                  <div className="section-container">
                    <Row className="g-3">
                      {sectionData.map((item, idx) => (
                        <Col xs="6" sm="4" md="3" lg="2" key={idx}>
                          {renderCard(item, currentTab === 3 ? 0 : currentTab)}
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
                <style>{`
                                    /* Section Layout */
                                    .section-container {
                                        margin-top: -2px;
                                        margin-bottom: 10px;
                                    }

                                    /* Card Base Styles */
                                    .custom-card {
                                        border-radius: 12px;
                                        transition: transform 0.3s ease-in-out;
                                        height: 120px;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        margin-bottom: 18px;
                                        margin-right: -10px;
                                    }

                                    .custom-card:hover {
                                        transform: scale(1.05);
                                        pointer: cursor;
                                    }

                                    /* Card Variants */
                                    .emr-card, .most-used-card {
                                        background: rgb(237, 240, 246);
                                    }

                                    .note-card {
                                        background: linear-gradient(135deg, #A0B4F1, #8EE4AF);
                                    }

                                    /* Card Content */
                                    .emr-content, .note-content, .most-used-content {
                                        font-size: 0.7rem;
                                        display: flex;
                                        flex-direction: column;
                                        align-items: center;
                                        text-align: center;
                                    }

                                    /* Image Styles */
                                    .emr-logo, .note-image, .most-used-emr-logo {
                                        width: 80px;
                                        height: 80px;
                                        object-fit: contain;
                                    }

                                    /* Text Styles */
                                    .note-name, .most-used-text {
                                        font-size: 0.7rem;
                                        margin-top: 5px;
                                        text-align: center;
                                    }

                                    /* Responsive Styles */
                                    @media (max-width: 768px) {
                                        .custom-card {
                                            height: 100px;
                                            padding: 5px;
                                        }
                                        .note-name, .most-used-text {
                                            font-size: 0.6rem;
                                        }
                                        .emr-logo, .note-image, .most-used-emr-logo {
                                            width: 50px;
                                            height: 50px;
                                        }
                                    }
                                `}</style>
              </Container>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default TemplateLibraryBrowseByCategory;
